import { TypeAheadAvailableNodes } from '@atlaskit/editor-common/type-ahead';
import { pluginKey as typeAheadPluginKey } from './pm-plugins/key';
import { updateSelectedIndex } from './commands/update-selected-index';
import { StatsModifier } from './stats-modifier';
import { typeAheadListMessages } from './messages';
export var isTypeAheadHandler = function isTypeAheadHandler(handler) {
  return handler && Object.values(TypeAheadAvailableNodes).includes(handler.id) && typeof handler.trigger === 'string' && typeof handler.selectItem === 'function' && typeof handler.getItems === 'function';
};

/** Is a typeahead plugin open? */
export var isTypeAheadOpen = function isTypeAheadOpen(editorState) {
  var _typeAheadPluginKey$g, _typeAheadPluginKey$g2;
  return !!(typeAheadPluginKey !== null && typeAheadPluginKey !== void 0 && (_typeAheadPluginKey$g = typeAheadPluginKey.getState(editorState)) !== null && _typeAheadPluginKey$g !== void 0 && (_typeAheadPluginKey$g2 = _typeAheadPluginKey$g.decorationSet) !== null && _typeAheadPluginKey$g2 !== void 0 && _typeAheadPluginKey$g2.find().length);
};
export var getPluginState = function getPluginState(editorState) {
  return typeAheadPluginKey.getState(editorState);
};
export var getTypeAheadHandler = function getTypeAheadHandler(editorState) {
  var _typeAheadPluginKey$g3;
  return (_typeAheadPluginKey$g3 = typeAheadPluginKey.getState(editorState)) === null || _typeAheadPluginKey$g3 === void 0 ? void 0 : _typeAheadPluginKey$g3.triggerHandler;
};
export var getTypeAheadQuery = function getTypeAheadQuery(editorState) {
  var _typeAheadPluginKey$g4;
  return (_typeAheadPluginKey$g4 = typeAheadPluginKey.getState(editorState)) === null || _typeAheadPluginKey$g4 === void 0 ? void 0 : _typeAheadPluginKey$g4.query;
};
export var isTypeAheadAllowed = function isTypeAheadAllowed(state) {
  var isOpen = isTypeAheadOpen(state);
  // if the TypeAhead is open
  // we should not allow it
  return !isOpen;
};
export var findHandler = function findHandler(id, state) {
  var pluginState = typeAheadPluginKey.getState(state);
  if (!pluginState || !pluginState.typeAheadHandlers || pluginState.typeAheadHandlers.length === 0) {
    return null;
  }
  var typeAheadHandlers = pluginState.typeAheadHandlers;
  return typeAheadHandlers.find(function (h) {
    return h.id === id;
  }) || null;
};
export var findHandlerByTrigger = function findHandlerByTrigger(_ref) {
  var trigger = _ref.trigger,
    editorState = _ref.editorState;
  var pluginState = typeAheadPluginKey.getState(editorState);
  if (!pluginState || !pluginState.typeAheadHandlers || pluginState.typeAheadHandlers.length === 0) {
    return null;
  }
  var typeAheadHandlers = pluginState.typeAheadHandlers;
  return typeAheadHandlers.find(function (h) {
    return h.trigger === trigger;
  }) || null;
};
export var moveSelectedIndex = function moveSelectedIndex(_ref2) {
  var editorView = _ref2.editorView,
    direction = _ref2.direction;
  return function () {
    var typeAheadState = getPluginState(editorView.state);
    if (!typeAheadState) {
      return;
    }
    var selectedIndex = typeAheadState.selectedIndex,
      items = typeAheadState.items;
    var stats = typeAheadState.stats instanceof StatsModifier ? typeAheadState.stats : new StatsModifier();
    var nextIndex;
    if (direction === 'next') {
      stats.increaseArrowDown();

      /**
       * See: https://product-fabric.atlassian.net/browse/ED-17200
       * `selectedIndex` is forced to -1 now to not immediately focus the typeahead
       * and only do so when there is explicit logic to focus into the typeahead
       * options.
       *
       * This check for "set index to 1 when -1"
       * - is a temporary workaround to get back the previous behaviour without
       *    entirely reverting the a11y improvements
       *
       */
      if (selectedIndex === -1 && items.length > 1) {
        nextIndex = 1;
      } else {
        nextIndex = selectedIndex >= items.length - 1 ? 0 : selectedIndex + 1;
      }
    } else {
      stats.increaseArrowUp();
      nextIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
    }
    updateSelectedIndex(nextIndex)(editorView.state, editorView.dispatch);
  };
};
export var getTypeAheadListAriaLabels = function getTypeAheadListAriaLabels(trigger, intl, item) {
  var _item$mention, _item$mention2, _item$emoji, _item$emoji2, _item$emoji3;
  switch (trigger) {
    case '@':
      return {
        popupAriaLabel: intl.formatMessage(typeAheadListMessages.mentionPopupLabel),
        listItemAriaLabel: intl.formatMessage(typeAheadListMessages.metionListItemLabel, {
          name: (item === null || item === void 0 ? void 0 : (_item$mention = item.mention) === null || _item$mention === void 0 ? void 0 : _item$mention.name) || '',
          shortName: (item === null || item === void 0 ? void 0 : (_item$mention2 = item.mention) === null || _item$mention2 === void 0 ? void 0 : _item$mention2.mentionName) || ''
        })
      };
    case '/':
      return {
        popupAriaLabel: intl.formatMessage(typeAheadListMessages.quickInsertPopupLabel),
        listItemAriaLabel: intl.formatMessage(typeAheadListMessages.emojiListItemLabel, {
          name: (item === null || item === void 0 ? void 0 : item.title) || '',
          shortcut: (item === null || item === void 0 ? void 0 : (_item$emoji = item.emoji) === null || _item$emoji === void 0 ? void 0 : _item$emoji.shortName) || ''
        })
      };
    case ':':
      return {
        popupAriaLabel: intl.formatMessage(typeAheadListMessages.emojiPopupLabel),
        listItemAriaLabel: intl.formatMessage(typeAheadListMessages.emojiListItemLabel, {
          name: (item === null || item === void 0 ? void 0 : (_item$emoji2 = item.emoji) === null || _item$emoji2 === void 0 ? void 0 : _item$emoji2.name) || '',
          shortcut: (item === null || item === void 0 ? void 0 : (_item$emoji3 = item.emoji) === null || _item$emoji3 === void 0 ? void 0 : _item$emoji3.shortName) || ''
        })
      };
    default:
      return {
        popupAriaLabel: intl.formatMessage(typeAheadListMessages.typeAheadPopupLabel)
      };
  }
};